import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import './Mileage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Mileage = ({ onBack, user }) => {
  const [file, setFile] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [accountByAddress, setAccountByAddress] = useState({});
  const [dateByAddress, setDateByAddress] = useState({});
  const [results, setResults] = useState(null);
  const [officeAddress, setOfficeAddress] = useState('12600 Westlinks Dr, Fort Myers, FL 33916');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Numeric timestamp for sorting. Missing dates sort last.
  const toTimestamp = (value) => {
    if (value === undefined || value === null || value === '') return Number.POSITIVE_INFINITY;
    if (typeof value === 'number' && value > 36526 && value < 54789) {
      // Excel serial date: days since 1899-12-30
      return (value - 25569) * 86400000;
    }
    const parsed = Date.parse(String(value));
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };

  const formatDate = (value) => {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'number' && value > 36526 && value < 54789) {
      try {
        const d = XLSX.SSF.parse_date_code(value);
        if (d) return `${String(d.m).padStart(2, '0')}/${String(d.d).padStart(2, '0')}/${d.y}`;
      } catch (e) { /* fall through */ }
    }
    const str = String(value);
    const match = str.match(/^(\d{1,2}\/\d{1,2}\/\d{4})/);
    return match ? match[1] : str;
  };

  const readExcelFile = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(sheet));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(f);
  });

  const handleFileUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setError('');
    setResults(null);

    try {
      const rows = await readExcelFile(f);
      const seen = new Set();
      const uniq = [];
      const accountMap = {};
      const dateMap = {};
      const sortKeyMap = {};
      rows.forEach((r) => {
        const addr = r['Location ID: Location ID'] || r['Location ID'];
        if (!addr) return;
        const cleanAddr = String(addr).trim();
        const key = cleanAddr.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        uniq.push(cleanAddr);
        const account = r['Account Name'] || r['Account: Account Name'] || r['Account'] || '';
        accountMap[cleanAddr] = String(account).trim();
        const created = r['Created Date'] || r['Opportunity: Created Date'] || r['Create Date'] || '';
        dateMap[cleanAddr] = formatDate(created);
        sortKeyMap[cleanAddr] = toTimestamp(created);
      });

      uniq.sort((a, b) => sortKeyMap[a] - sortKeyMap[b]);

      if (uniq.length === 0) {
        setError('No "Location ID" column found (or it is empty).');
        setAddresses([]);
        setAccountByAddress({});
        setDateByAddress({});
        return;
      }
      setAddresses(uniq);
      setAccountByAddress(accountMap);
      setDateByAddress(dateMap);
    } catch (err) {
      setError(err.message || 'Failed to read spreadsheet.');
      setAddresses([]);
    }
  };

  const calculateMileage = async () => {
    if (addresses.length === 0) return;
    setProcessing(true);
    setError('');
    setResults(null);

    try {
      const response = await fetch(`${API_URL}/api/mileage/calculate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': user.userId,
        },
        body: JSON.stringify({ addresses }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      setOfficeAddress(data.office_address);
      setResults(data.results);
    } catch (err) {
      setError(err.message || 'Calculation failed.');
    } finally {
      setProcessing(false);
    }
  };

  const totals = useMemo(() => {
    if (!results) return null;
    let oneWay = 0, round = 0, ok = 0, failed = 0;
    results.forEach((r) => {
      if (r.status === 'OK') {
        oneWay += r.one_way_miles || 0;
        round += r.round_trip_miles || 0;
        ok += 1;
      } else {
        failed += 1;
      }
    });
    return {
      oneWay: Math.round(oneWay * 10) / 10,
      round: Math.round(round * 10) / 10,
      ok,
      failed,
    };
  }, [results]);

  const buildCopyText = () => {
    if (!results) return '';
    const header = ['Created Date', 'Account', 'Office', 'Customer Address', 'One-Way (mi)', 'Round-Trip (mi)'].join('\t');
    const lines = results.map((r) => [
      dateByAddress[r.address] || '',
      accountByAddress[r.address] || '',
      officeAddress,
      r.address,
      r.status === 'OK' ? r.one_way_miles : r.status,
      r.status === 'OK' ? r.round_trip_miles : '',
    ].join('\t'));
    return [header, ...lines].join('\n');
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Clipboard copy failed — select rows manually.');
    }
  };

  return (
    <div className="mileage">
      <div className="mileage-header">
        <button onClick={onBack} className="back-button">← Back to Menu</button>
        <h2>Mileage Calculator</h2>
      </div>

      <div className="mileage-office">
        <span className="mileage-office-label">Office:</span>
        <span className="mileage-office-value">{officeAddress}</span>
      </div>

      <div className="mileage-upload">
        <label className="mileage-file-label">
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={handleFileUpload}
            className="mileage-file-input"
          />
          <span className="mileage-file-btn">Choose Opportunity Spreadsheet</span>
        </label>
        {file && (
          <div className="mileage-file-info">
            ✓ {file.name} — {addresses.length} unique address{addresses.length === 1 ? '' : 'es'}
          </div>
        )}
      </div>

      {error && <div className="mileage-error">{error}</div>}

      {addresses.length > 0 && !results && (
        <div className="mileage-preview">
          <h3>Addresses from "Location ID" column</h3>
          <ul className="mileage-address-list">
            {addresses.map((a, i) => (
              <li key={i}>
                {dateByAddress[a] && <span className="mileage-date">{dateByAddress[a]}</span>}
                {accountByAddress[a] && <strong className="mileage-account">{accountByAddress[a]}</strong>}
                <span>{a}</span>
              </li>
            ))}
          </ul>
          <button
            className="mileage-calc-btn"
            onClick={calculateMileage}
            disabled={processing}
          >
            {processing ? 'Calculating…' : `Calculate Mileage for ${addresses.length} addresses`}
          </button>
        </div>
      )}

      {results && (
        <div className="mileage-results">
          <div className="mileage-results-header">
            <h3>Results</h3>
            <button className="mileage-copy-btn" onClick={copyAll}>
              {copied ? '✓ Copied!' : 'Copy All (tab-separated)'}
            </button>
          </div>

          {totals && (
            <div className="mileage-totals">
              <span><strong>{totals.ok}</strong> calculated</span>
              {totals.failed > 0 && <span className="mileage-failed">{totals.failed} failed</span>}
              <span>Total one-way: <strong>{totals.oneWay} mi</strong></span>
              <span>Total round-trip: <strong>{totals.round} mi</strong></span>
            </div>
          )}

          <table className="mileage-table">
            <thead>
              <tr>
                <th>Created Date</th>
                <th>Account</th>
                <th>Office</th>
                <th>Customer Address</th>
                <th className="num">One-Way (mi)</th>
                <th className="num">Round-Trip (mi)</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className={r.status === 'OK' ? '' : 'mileage-row-error'}>
                  <td className="mileage-date-cell">{dateByAddress[r.address] || ''}</td>
                  <td className="mileage-account-cell">{accountByAddress[r.address] || ''}</td>
                  <td className="mileage-office-cell">{officeAddress}</td>
                  <td>{r.address}</td>
                  <td className="num">{r.status === 'OK' ? r.one_way_miles : r.status}</td>
                  <td className="num">{r.status === 'OK' ? r.round_trip_miles : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Mileage;
